import { YardAlert, Alert, ALERT_TYPES } from "@/features/relayYardControl/types/alert";
import { Job, YardJob } from "@/features/relayYardControl/types/jobs";
import { Visit, YardVisit } from "@/features/relayYardControl/types/visits";
import {
  LocationData,
  YardDetectionDataState,
} from "@/features/relayYardControl/store/yardDetectionData";

// Constants for special location codes
export const SPECIAL_LOCATION_CODES = {
  NO_LOCATION: "NO_LOCATION",
  DISPLACED: "EQUIPMENT_IS_DISPLACED",
  IN_TRANSIT: "EQUIPMENT_IS_IN_TRANSIT",
  YARD_FLEET: "EQUIPMENT_IS_YARD_FLEET",
} as const;

export type SpecialLocationCode =
  (typeof SPECIAL_LOCATION_CODES)[keyof typeof SPECIAL_LOCATION_CODES];

/**
 * Creates a new empty location state with fresh array instances
 * This ensures each location gets its own independent arrays
 * @returns A new LocationData object with empty arrays
 */
export const createEmptyLocationState = (): LocationData => ({
  visits: [],
  jobs: [],
  alerts: [],
});

/**
 * Resets location data arrays in the state
 * If a key is provided, only that specific array (visits/jobs/alerts) is reset
 * If no key is provided, all arrays for the location are reset
 * @param state - Current yard detection state
 * @param key - Optional key to specify which array to reset
 * @returns Updated state with reset arrays
 */
export const applyEmptyArrayToLocationCodeData = ({
  state,
  key,
}: {
  state: YardDetectionDataState;
  key?: keyof LocationData;
}): YardDetectionDataState => {
  const newState = { ...state };
  const newData = { ...newState.data };

  if (key) {
    Object.keys(newData).forEach((locationCode) => {
      if (newData[locationCode][key]) {
        newData[locationCode] = {
          ...newData[locationCode],
          [key]: [],
        };
      }
    });
  } else {
    Object.keys(newData).forEach((locationCode) => {
      if (newData[locationCode]) {
        newData[locationCode] = createEmptyLocationState();
      }
    });
  }

  newState.data = newData;
  return newState;
};

/**
 * Initializes data structure for a location in the state
 * If location doesn't exist:
 *   - Creates a new location with all arrays (visits, jobs, alerts) initialized as empty
 * If location exists but specified array is missing:
 *   - Initializes just that array as empty while preserving other arrays
 *
 * @param state - Current yard detection state
 * @param locationCode - Location code to initialize
 * @param key - Key indicating which array to check for initialization
 * @returns New state with initialized location data
 */
export const initializeLocationData = (
  state: YardDetectionDataState,
  locationCode: string,
  key: keyof LocationData
): YardDetectionDataState => {
  const newState = { ...state };
  const newData = { ...newState.data };
  if (!newData[locationCode]) {
    newData[locationCode] = createEmptyLocationState();
  } else if (!newData[locationCode][key]) {
    newData[locationCode][key] = [];
  }
  newState.data = newData;
  return newState;
};

import { shouldUpdateBasedOnVersion } from "@/utils/versionUtils";
export { shouldUpdateBasedOnVersion };

export const RECORD_TYPES = {
  VISIT: "visit",
  JOB: "job",
  ALERT: "alert",
} as const;

export type RecordType = (typeof RECORD_TYPES)[keyof typeof RECORD_TYPES];

/**
 * Extracts location code from a record based on its type
 * @param record - Visit or Job record
 * @param type - Type of the record ("visit" or "job")
 * @returns Location code string or undefined if not found/invalid
 */
export const getLocationCode = (
  record: Visit | Job | Alert | undefined,
  type: RecordType
): string | undefined => {
  if (!record) return undefined;

  if (type === RECORD_TYPES.VISIT) {
    const visitRecord = record as Visit;
    if (visitRecord.data?.location?.identifier?.locationCode) {
      return visitRecord.data?.location?.identifier?.locationCode;
    } else if (visitRecord.data?.isDisplaced) {
      return SPECIAL_LOCATION_CODES.DISPLACED;
    } else if (visitRecord.data?.isInTransit) {
      return SPECIAL_LOCATION_CODES.IN_TRANSIT;
    } else if (visitRecord.data?.isYardFleet) {
      return SPECIAL_LOCATION_CODES.YARD_FLEET;
    }
  }

  if (type === RECORD_TYPES.JOB) {
    const jobRecord = record as Job;

    let startLocation = jobRecord.data?.job?.startLocation;

    // Handle case where startLocation is a string format JSON
    if (startLocation && typeof startLocation === "string") {
      try {
        startLocation = JSON.parse(startLocation);
      } catch (error) {
        console.error("Failed to parse startLocation JSON string:", error);
        return undefined;
      }
    }

    return startLocation?.code;
  }

  if (type === RECORD_TYPES.ALERT) {
    const alertRecord = record as Alert;
    const alertType = alertRecord?.data?.alert?.alertType;

    // Handle NO_LOCATION-based alerts (MISSING_CHECK_IN, VR)
    if (alertType === ALERT_TYPES.MISSING_CHECK_IN || alertType === ALERT_TYPES.VR) {
      return SPECIAL_LOCATION_CODES.NO_LOCATION;
    }

    // Handle LOCATION-based alerts (LOCATION, CONTENT)
    return alertRecord?.data?.location?.locationCode || SPECIAL_LOCATION_CODES.NO_LOCATION;
  }

  return undefined;
};

/**
 * Extracts visit ID from a visit record
 * Safely navigates the nested structure to find the ID
 * @param visit - Visit record to extract ID from
 * @returns Visit ID string or undefined if not found
 */
export const getVisitId = (visit: Visit | undefined): string | undefined => {
  return visit?.data?.equipment?.visitDetails?.visitId?.id;
};

/**
 * Updates an item across locations, handling location changes
 *
 * This function:
 * 1. Searches for the item by ID across all locations
 * 2. Removes it from its old location if found and if new version is higher
 * 3. Adds it to the new location with the updated data
 *
 * @param state - Current yard detection state
 * @param item - The item to update
 * @param key - Data type key ('visits', 'jobs', or 'alerts')
 * @param getLocationCodeFn - Function to extract location code from the item
 * @param getId - Function to extract ID from the item
 * @returns Updated state with the item moved to the correct location
 */
export const updateItemWithLocationChange = <T extends YardVisit | YardJob | YardAlert>({
  state,
  item,
  key,
  getLocationCodeFn,
  getId,
}: {
  state: YardDetectionDataState;
  item: T;
  key: keyof LocationData;
  getLocationCodeFn: (item: T) => string | undefined;
  getId: (item: T) => string | undefined;
}): YardDetectionDataState => {
  const newState = { ...state };
  const itemId = getId(item);
  const newLocationCode = getLocationCodeFn(item);

  // If no valid ID or location code, we can't process the update
  if (!itemId || !newLocationCode) return newState;

  // Get the new version for comparison
  const newVersion = item.datapathViewVersion || "0";

  // Flag to track if we should update
  let shouldUpdate = true;

  // First, find and remove the old item if it exists
  Object.keys(newState.data).forEach((locationCode) => {
    const locationData = newState.data[locationCode];

    // Skip if this location has no array for this key
    if (!locationData[key] || !Array.isArray(locationData[key])) return;

    // Find the item with matching ID in this location
    const itemArray = locationData[key] as T[];
    const itemIndex = itemArray.findIndex((existingItem) => getId(existingItem) === itemId);

    // If found, check version and remove if needed
    if (itemIndex >= 0) {
      const existingItem = itemArray[itemIndex];
      const existingVersion = existingItem.datapathViewVersion || "0";

      // Only update if new version is higher
      if (shouldUpdateBasedOnVersion(existingVersion, newVersion)) {
        // Remove the old item from this location
        // Use type assertion to avoid TypeScript error
        newState.data[locationCode][key] = [
          ...itemArray.slice(0, itemIndex),
          ...itemArray.slice(itemIndex + 1),
        ] as any;
      } else {
        // If the existing version is newer, don't update at all
        shouldUpdate = false;
      }
    }
  });

  // If we should update, add the item to the correct location
  if (shouldUpdate) {
    // Initialize the new location if it doesn't exist
    if (!newState.data[newLocationCode]) {
      newState.data[newLocationCode] = createEmptyLocationState();
    }

    // Initialize array if it doesn't exist
    if (!newState.data[newLocationCode][key]) {
      newState.data[newLocationCode][key] = [];
    }

    // Add the item to the correct location
    // Use type assertion to avoid TypeScript error
    const itemArray = newState.data[newLocationCode][key] as any[];
    itemArray.push(item);
  }

  return newState;
};

/**
 * Processes batch updates for a specific data type without race conditions
 * Only clears data for locations that will actually receive new data
 * Uses updateItemWithLocationChange for consistent item handling
 *
 * @param state - Current yard detection state (mutable via Immer)
 * @param items - Array of items to process
 * @param key - Data type key ('visits', 'jobs', or 'alerts')
 * @param getLocationCode - Function to extract location code from an item
 * @param getId - Function to extract ID from an item for deduplication
 * @param processItem - Optional function to transform item before adding (e.g., add datapathViewVersion)
 */
export const processBatchUpdatesWithoutRaceConditions = <
  T extends YardVisit | YardJob | YardAlert,
>({
  state,
  items,
  key,
  getLocationCode,
  getId,
  processItem = (item) => item,
}: {
  state: YardDetectionDataState;
  items: any[];
  key: keyof LocationData;
  getLocationCode: (item: any) => string | undefined;
  getId: (item: T) => string | undefined;
  processItem?: (item: any) => T;
}): void => {
  if (!Array.isArray(items)) return;

  // Step 1: Identify locations that will be updated
  const locationCodesToUpdate = new Set<string>();
  items.forEach((item) => {
    const locationCode = getLocationCode(item);
    if (locationCode) {
      locationCodesToUpdate.add(locationCode);
    }
  });

  // Step 2: Clear data only for locations that will receive new data
  locationCodesToUpdate.forEach((locationCode) => {
    if (state.data[locationCode]) {
      state.data[locationCode] = {
        ...state.data[locationCode],
        [key]: [],
      };
    }
  });

  // Step 3: Process each item using updateItemWithLocationChange
  items.forEach((item) => {
    const processedItem = processItem(item);

    // Use the provided getLocationCode function, or fall back to getLocationCode for non-alerts
    const locationCode = getLocationCode(item);

    // Use updateItemWithLocationChange to handle the item
    const updatedState = updateItemWithLocationChange({
      state,
      item: processedItem,
      key,
      getLocationCodeFn: () => locationCode,
      getId,
    });

    // Update the state data
    state.data = updatedState.data;
  });
};

/**
 * Resolves the display carrier for a visit.
 *
 * Matches the YMS yard-console rule: show the equipment's `ownerCode` with
 * an `"OTHR"` fallback when the owner is missing. YMS uses this across every
 * move/checkout screen (Request Move, Exit Gate checkout, Force Checkout,
 * candidate pickers) — see YMS `yard-asset-service.js` / `yard-state-repository.js`:
 *   `yardAsset.owner.code = yardAsset.owner.code || 'OTHR'`.
 *
 * This selector is **only** used inside the ActionPanel surfaces (Request Move
 * table, Exit Gate form table, and the Exit Gate "Add vehicle" candidate
 * dropdown that feeds the form). Other RYC surfaces (trailer detail panel,
 * equipment card) intentionally continue to display the hauling `operator`
 * via their own extractors (`trailerDataExtractor.ts`, `equipmentDataExtractor.ts`).
 *
 * See Yard-5762 for the YMS-matching rationale.
 */
export const resolveCarrier = (visit: Visit): string => {
  const equipment = visit.data?.equipment;
  return equipment?.equipmentDetails?.ownerCode || "OTHR";
};

export const resolveOperator = (visit: Visit): string => {
  const equipment = visit.data?.equipment;
  return equipment?.operator || "OTHR";
};

export const resolveVRID = (visit: Visit): string | undefined => {
  const equipment = visit.data?.equipment;
  const activeVisitReasons = equipment?.visitDetails?.activeVisitReasons || [];
  const reason = activeVisitReasons[0];
  return reason?.haulIdentifier?.vrHaulIdentifiers?.vrId;
};
